import * as moment from 'moment';
import { AppConfig } from '../../app.config';
import { FileReaderHelper } from './file-reader.helper';

export enum PathInfoType {
    UNKNOWN,
    BLOB,
    FILESYSTEM,
    DATA_URI
}

export interface PathInfo {
    type: PathInfoType;
    path: string;
    persistent: boolean;
    cordova: boolean;
}

export enum MediaType {
    image,
    audio,
    video,
    pdf,
    other
}

/**
 * FileHelper gives us static methods for dealing with single files.
 *
 * The methods here should be simple wrappers around native browser APIs and
 * should NEVER have any dependencies on Angular or Ionic.
 */
export class FileHelper {

    /**
     * format of ObjectUrl in desktop OS's persistent file system - which we don't have direct access to
     *   blob:http://domain.com/00000000-0000-0000-0000-000000000000
     *
     * format of file stored in desktop browser's persistent file system
     *   filesystem:http://domain.com/persistent/00000000-0000-0000-0000-000000000000
     *
     * format of file stored in mobile app's file system e.g. when the file is selected by the gallery app
     *   file:///data/user/0/io.ironcode.vas/files/00000000-0000-0000-0000-000000000000
     *
     * format of cordova file
     *   cdvfile://localhost/files/00000000-0000-0000-0000-000000000000
     */
    static parsePath(path: string = ''): PathInfo {

        switch (true) {
            case path.startsWith('data:'): {
                return {
                    cordova: false,
                    path,
                    persistent: false,
                    type: PathInfoType.DATA_URI
                };
            }
            // object url - the value is only valid while the document that generated exists e.g. until we exit the page
            case path.startsWith('blob:'): {
                return {
                    cordova: false,
                    path,
                    persistent: false,
                    type: PathInfoType.BLOB
                };
            }
            // desktop browser's file system
            case path.startsWith('filesystem:'): {
                return {
                    cordova: false,
                    path,
                    // @todo check the path for persistent or temporary in the path
                    persistent: true,
                    type: PathInfoType.FILESYSTEM
                };
            }
            // mobile app's file system
            case path.startsWith('file:'): {
                return {
                    cordova: true,
                    path,
                    persistent: true,
                    type: PathInfoType.FILESYSTEM
                };
            }
            // mobile app's file system
            case path.startsWith('cdvfile:'): {
                return {
                    cordova: true,
                    path,
                    persistent: true,
                    type: PathInfoType.FILESYSTEM
                };
            }
            default: {
                return {
                    cordova: undefined,
                    path,
                    persistent: undefined,
                    type: PathInfoType.UNKNOWN
                };
            }
        }
    }

    /**
     * In most cases Blobs already have their type set by the browser. However in some cases, like outlook emails the
     * browser will not detect the mime type so this method uses getMimeTypeForFileSignature which has a little extra
     * logic to catch those cases.
     */
    static async getMimeTypeForBlob(blob: Blob): Promise<string> {

        if (blob.type) {
            return blob.type;
        }

        const fileReader = FileReaderHelper.getFileReader();

        return new Promise<string>((resolve, reject) => {
            fileReader.onloadend = e => {
                const bytes = (new Uint8Array((e.target as any).result)).subarray(0, 8);
                let signature = '';
                bytes.forEach(byte => signature += byte.toString(16));
                resolve(FileHelper.getMimeTypeForFileSignature(signature));
            };
            fileReader.onerror = reject;
            fileReader.readAsArrayBuffer(blob);
        });
    }

    static getMimeTypeForFileSignature(signature: string): string {
        switch (true) {
            case !!signature.toUpperCase().match('D0CF11E0A1B11AE1'): {
                return 'application/vnd.ms-outlook';
            }
            default: {
                return '';
            }
        }
    }

    static blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = FileReaderHelper.getFileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                resolve(dataUrl);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /**
     * Copied from https://stackoverflow.com/a/12300351
     */
    static dataUrlToBlob(objectUrl: string): Blob {

        const pathInfo = FileHelper.parsePath(objectUrl);

        if (pathInfo.type !== PathInfoType.DATA_URI) {
            throw new Error('not a data uri');
        }

        // convert base64 to raw binary data held in a string
        // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
        const byteString = atob(objectUrl.split(',')[1]);

        // separate out the mime component
        const mimeString = FileHelper.dataUrlToMime(objectUrl);

        // write the bytes of the string to an ArrayBuffer
        const ab = new ArrayBuffer(byteString.length);

        // create a view into the buffer
        const ia = new Uint8Array(ab);

        // set the bytes of the buffer to the correct values
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        // write the ArrayBuffer to a blob, and you're done
        return new Blob([ab], {type: mimeString});
    }

    static dataUrlToMime(objectUrl: string): string {

        const pathInfo = FileHelper.parsePath(objectUrl);

        if (pathInfo.type !== PathInfoType.DATA_URI) {
            throw new Error('not a data uri');
        }

        return objectUrl.split(',')[0].split(':')[1].split(';')[0];
    }

    /**
     * This method allows us to create a preview url. A preview url is the value
     * we should put as the src attribute of an img tag when we want to display
     * the media to the user. For images, we can just use the value of the url
     * argument, but for everything else we want to map to one of our
     * placeholder images that represent the mimetype.
     *
     * @param mimeType {string} the mime type of the url
     * @param url {string} the url to the media resource
     */
    static getPreviewUrl(mimeType: string = '', url: string): string {
        switch (true) {
            case mimeType === null: {
                return 'assets/content-types/file.svg';
            }
            case mimeType.startsWith('image/'): {
                return url;
            }
            case mimeType.startsWith('video/'): {
                return 'assets/content-types/video-placeholder.png';
            }
            case mimeType.startsWith('audio/'): {
                return 'assets/content-types/mp3.png';
            }
            case mimeType === 'application/zip': {
                return 'assets/content-types/zip.svg';
            }
            case mimeType === 'application/pdf': {
                return 'assets/content-types/pdf.svg';
            }
            case mimeType.startsWith('text/'):
            case mimeType === 'application/sql': {
                return 'assets/content-types/txt.svg';
            }
            case mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            case mimeType === 'application/vnd.ms-excel' : {
                return 'assets/content-types/xls.svg';
            }
            case mimeType === 'application/msword':
            case mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            case mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
            case mimeType === 'application/vnd.ms-word.document.macroEnabled.12': {
                return 'assets/content-types/doc.svg';
            }
            default: {
                return 'assets/content-types/file.svg';
            }
        }
    }

    /**
     * Generate a name for an image. The generated name resembles the same
     * format one would expect to be generated on a phone
     *
     * @return {string}
     */
    static makeImageName(): string {
        return [
            'IMG_',
            moment().utc().format(AppConfig.DATE_FORMATS.IMG_NAME),
            '.jpg'
        ].join('');
    }

    static mimeTypeToMediaType(mimeType: string = ''): MediaType {

        switch (true) {
            case mimeType === null: {
                return MediaType.other;
            }
            case mimeType.startsWith('audio'): {
                return MediaType.video;
            }
            case mimeType.startsWith('image'): {
                return MediaType.image;
            }
            case mimeType.startsWith('video'): {
                return MediaType.video;
            }
            case mimeType === 'application/pdf': {
                return MediaType.pdf;
            }
            default: {
                return MediaType.other;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/file-system/file.helper.ts